import { DataService } from "@/services/data";
import { FavoriteType } from "@/types/user/favorite";
import { getObjectMetadata } from "@/utils/content-types";
import { LoaderCircle, StarOff } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface FavoritesProps {
    objectType: string;
}

export function Favorites({ objectType }: FavoritesProps) {
    const [favorites, setFavorites] = useState<FavoriteType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Récupérer les métadonnées de l'objet une seule fois
    const objectMetadata = getObjectMetadata(objectType);

    useEffect(() => {
        const loadFavorites = async () => {
            setLoading(true);
            try {
                const response = await DataService.client.get(`/users/favorites/?reference_type=${objectType}`).json<{ results: FavoriteType[] }>();
                const allFavorites = Array.isArray(response.results) ? response.results : [];

                // Filtrer les favoris pour ce type d'objet spécifique
                const filteredFavorites = allFavorites.filter(fav => {
                    if (!fav.content_type || !objectMetadata.contentTypeId) return false;

                    // Comparer le content_type du favori avec le contentTypeId des métadonnées
                    const favContentTypeId = typeof fav.content_type === "object" ? fav.content_type.id : fav.content_type;
                    return favContentTypeId == objectMetadata.contentTypeId;
                });

                setFavorites(filteredFavorites);
                setError(null);
            } catch (err) {
                console.error("Error loading favorites:", err);
                setError("Error loading favorites");
                setFavorites([]);
            } finally {
                setLoading(false);
            }
        };

        if (objectType && objectMetadata.contentTypeId) {
            loadFavorites();
        } else {
            setLoading(false);
            setFavorites([]);
        }
    }, [objectType, objectMetadata.contentTypeId]);

    // Supprimer un favori
    const handleDeleteFavorite = async (favoriteId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            await DataService.client.delete(`/users/favorites/${favoriteId}/`);
            setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
            toast.success("Favorite removed");
        } catch (error) {
            toast.error("Could not remove this favorite");
            console.error("Error removing favorite:", error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-2">
                <LoaderCircle className="h-4 w-4 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-2 py-1.5 italic text-red-400">
                <p className="text-sm">Error loading favorites</p>
            </div>
        );
    }

    if (!favorites || favorites.length === 0) {
        return (
            <div className="px-2 py-1.5 italic text-gray-300">
                <p className="text-sm">No favorites</p>
            </div>
        );
    }

    return (
        <>
            {favorites.map(favorite => {
                const IconComponent = objectMetadata.icon;
                return (
                    <div key={favorite.id} className="group relative overflow-hidden rounded-md hover:bg-gray-100">
                        <Link
                            className="flex cursor-pointer flex-row items-center gap-2 p-1.5 px-2 text-sm text-gray-800 hover:text-black"
                            to={favorite.url.replace(window.location.origin, "")}
                            title={favorite.name}
                        >
                            <div className="flex h-6 w-6 items-center justify-center rounded-sm px-1 text-sm font-medium" style={{ backgroundColor: objectMetadata.color }}>
                                <IconComponent size={14} className="text-white" strokeWidth={2} />
                            </div>
                            <div className="max-w-48 truncate">{favorite.name}</div>
                            <div
                                className="ml-auto flex h-4 w-4 flex-shrink-0 items-center justify-center text-gray-400 opacity-0 hover:text-red-600 group-hover:opacity-100"
                                onClick={e => handleDeleteFavorite(favorite.id, e)}
                                title="Remove from favorites"
                            >
                                <StarOff size={14} />
                            </div>
                        </Link>
                    </div>
                );
            })}
        </>
    );
}
